import { useMutation } from "@tanstack/vue-query";
import Report from "@/services/analytics/report";
import type { OpportunityDashboardRequestBody } from "./useOpportunityDashboardFilters";

function generateCSVFromData(data: any): { blob: Blob; filename: string } {
  const formatCurrency = (value: any) => `$${Number(value || 0).toFixed(2)}`;
  const formatPercent = (value: any) => `${Number(value || 0).toFixed(2)}%`;
  const formatDays = (value: any) => `${Math.round(value || 0)}d`;

  const csvRows = [
    [
      "Metric",
      "Current Value",
      "Previous Value",
      "Change %",
      "Compare To Value",
      "Compare To Change %",
    ],
    [
      "Total Meetings Held",
      data.totalMeetingsHeld?.toString() || "0",
      data.previousTotalMeetingsHeld?.toString() || "0",
      formatPercent(data.totalMeetingsHeldChangePercent),
      data.compareToTotalMeetingsHeld?.toString() || "N/A",
      data.compareToTotalMeetingsHeldChangePercent
        ? formatPercent(data.compareToTotalMeetingsHeldChangePercent)
        : "N/A",
    ],
    [
      "Total Pipeline Value (Weighted)",
      formatCurrency(data.totalPipelineValue),
      formatCurrency(data.previousTotalPipelineValue),
      formatPercent(data.totalPipelineValueChangePercent),
      data.compareToTotalPipelineValue ? formatCurrency(data.compareToTotalPipelineValue) : "N/A",
      data.compareToTotalPipelineValueChangePercent
        ? formatPercent(data.compareToTotalPipelineValueChangePercent)
        : "N/A",
    ],
    [
      "Closed Won Revenue",
      formatCurrency(data.closedWonRevenue),
      formatCurrency(data.previousClosedWonRevenue),
      formatPercent(data.closedWonRevenueChangePercent),
      data.compareToClosedWonRevenue ? formatCurrency(data.compareToClosedWonRevenue) : "N/A",
      data.compareToClosedWonRevenueChangePercent
        ? formatPercent(data.compareToClosedWonRevenueChangePercent)
        : "N/A",
    ],
    [
      "Win Rate",
      formatPercent(data.winRate),
      formatPercent(data.previousWinRate),
      formatPercent(data.winRateChangePercent),
      data.compareToWinRate ? formatPercent(data.compareToWinRate) : "N/A",
      data.compareToWinRateChangePercent
        ? formatPercent(data.compareToWinRateChangePercent)
        : "N/A",
    ],
    [
      "Average Deal Size",
      formatCurrency(data.averageDealSize),
      formatCurrency(data.previousAverageDealSize),
      formatPercent(data.averageDealSizeChangePercent),
      data.compareToAverageDealSize ? formatCurrency(data.compareToAverageDealSize) : "N/A",
      data.compareToAverageDealSizeChangePercent
        ? formatPercent(data.compareToAverageDealSizeChangePercent)
        : "N/A",
    ],
    [
      "Opportunities",
      data.opportunities?.toString() || "0",
      data.previousOpportunities?.toString() || "0",
      formatPercent(data.opportunitiesChangePercent),
      data.compareToOpportunities?.toString() || "N/A",
      data.compareToOpportunitiesChangePercent
        ? formatPercent(data.compareToOpportunitiesChangePercent)
        : "N/A",
    ],
    [
      "Average Time to Win",
      formatDays(data.averageTimeToWin),
      formatDays(data.previousAverageTimeToWin),
      formatPercent(data.averageTimeToWinChangePercent),
      data.compareToAverageTimeToWin ? formatDays(data.compareToAverageTimeToWin) : "N/A",
      data.compareToAverageTimeToWinChangePercent
        ? formatPercent(data.compareToAverageTimeToWinChangePercent)
        : "N/A",
    ],
    [
      "Sales Velocity",
      formatCurrency(data.salesVelocity),
      formatCurrency(data.previousSalesVelocity),
      formatPercent(data.salesVelocityChangePercent),
      data.compareToSalesVelocity ? formatCurrency(data.compareToSalesVelocity) : "N/A",
      data.compareToSalesVelocityChangePercent
        ? formatPercent(data.compareToSalesVelocityChangePercent)
        : "N/A",
    ],
  ];

  const csvContent = csvRows.map((row) => row.map((cell) => `"${cell}"`).join(",")).join("\n");
  const blob = new Blob([csvContent], { type: "text/csv" });
  const filename = `opportunity-dashboard-${new Date().toISOString().split("T")[0]}.csv`;
  return { blob, filename };
}

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export function useExportOpportunityCSV() {
  return useMutation({
    mutationFn: async (requestBody: OpportunityDashboardRequestBody) => {
      const dashboardResponse = await Report.getOpportunityDashboard(requestBody);

      if (!dashboardResponse.data?.status || !dashboardResponse.data?.data) {
        throw new Error("Failed to fetch dashboard data");
      }

      const dashboardData = dashboardResponse.data.data;

      try {
        const csvResponse = await Report.exportOpportunityDashboardCSV(dashboardData);

        const contentType = csvResponse.headers["content-type"] || "";
        const isExcel = contentType.includes("spreadsheetml") || contentType.includes("excel");
        const fileExtension = isExcel ? "xlsx" : "csv";
        const blob = new Blob([csvResponse.data], {
          type: isExcel
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "text/csv",
        });
        const filename = `opportunity-dashboard-${new Date().toISOString().split("T")[0]}.${fileExtension}`;

        downloadBlob(blob, filename);

        return { type: isExcel ? "Excel" : "CSV" } as const;
      } catch {
        // Fallback to client-side CSV generation
        const { blob, filename } = generateCSVFromData(dashboardData);
        downloadBlob(blob, filename);
        return { type: "CSV" } as const;
      }
    },
  });
}
